import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

export default function SettingsPage() {
  const { user } = useAuth();
  const [apiKey] = useState(user?.apiKey || '');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSaveSettings = async () => {
    setSaving(true);
    setMessage(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setMessage({ type: 'success', text: 'Copied to clipboard!' });
    setTimeout(() => setMessage(null), 2000);
  };

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
        <p className="text-text-secondary">Manage your account and API credentials</p>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-success-light text-success' : 'bg-danger-light text-danger'}`}>
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        {/* API Credentials */}
        <div className="bg-card border border-border-default rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-text-primary mb-4">API Credentials</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Tenant ID
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={user?.tenantId || ''}
                  readOnly
                  className="flex-1 px-3 py-2 bg-bg-secondary border border-border-default rounded-lg text-text-muted font-mono text-sm"
                />
                <button
                  onClick={() => copyToClipboard(user?.tenantId || '')}
                  className="px-3 py-2 bg-bg-secondary border border-border-default rounded-lg hover:bg-bg-tertiary transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                API Key
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={apiKey}
                  readOnly
                  className="flex-1 px-3 py-2 bg-bg-secondary border border-border-default rounded-lg text-text-muted font-mono text-sm"
                />
                <button
                  onClick={() => copyToClipboard(apiKey)}
                  className="px-3 py-2 bg-bg-secondary border border-border-default rounded-lg hover:bg-bg-tertiary transition-colors"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-text-muted mt-1">
                Use this key in the X-API-Key header for authenticated requests
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Webhook Secret
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={user?.webhookSecret || ''}
                  readOnly
                  className="flex-1 px-3 py-2 bg-bg-secondary border border-border-default rounded-lg text-text-muted font-mono text-sm"
                />
                <button
                  onClick={() => copyToClipboard(user?.webhookSecret || '')}
                  className="px-3 py-2 bg-bg-secondary border border-border-default rounded-lg hover:bg-bg-tertiary transition-colors"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Webhook Configuration */}
        <div className="bg-card border border-border-default rounded-xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Webhook Configuration</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-1">
                Webhook URL
              </label>
              <input
                type="url"
                value={webhookUrl}
                onChange={(e) => setWebhookUrl(e.target.value)}
                className="input"
                placeholder="https://your-domain.com/webhook"
              />
              <p className="text-xs text-text-muted mt-1">
                Payments and events will be sent to this URL
              </p>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
